import openai from './openaiClient';
import { 
  APIConnectionError,
  AuthenticationError,
  PermissionDeniedError,
  RateLimitError,
  InternalServerError
} from 'openai';

/**
 * Maps OpenAI API error types to user-friendly error messages.
 * @param {Error} error - The error object from OpenAI API.
 * @returns {Object} Error information with isInternal flag and message.
 */
function getErrorMessage(error) {
  if (error instanceof AuthenticationError) {
    return { isInternal: true, message: 'Invalid API key or authentication failed. Please check your OpenAI API key.' };
  } else if (error instanceof PermissionDeniedError) {
    return { isInternal: true, message: 'Quota exceeded or authorization failed. You may have exceeded your usage limits or do not have access to this resource.' };
  } else if (error instanceof RateLimitError) {
    return { isInternal: true, message: 'Rate limit exceeded. You are sending requests too quickly. Please wait a moment and try again.' };
  } else if (error instanceof InternalServerError) {
    return { isInternal: true, message: 'OpenAI service is currently unavailable. Please try again later.' };
  } else if (error instanceof APIConnectionError) {
    return { isInternal: true, message: 'Unable to connect to OpenAI service. Please check your API key and internet connection.' };
  } else {
    return { isInternal: false, message: error?.message || 'An unexpected error occurred. Please try again.' };
  }
}

/**
 * Converts a natural language query into structured filter parameters.
 * @param {string} query - The natural language search query from the user.
 * @returns {Promise<Object>} Structured filter object with search, stage, industry, and sort fields.
 */
export async function parseNaturalLanguageQuery(query) {
  try {
    const response = await openai?.chat?.completions?.create({
      model: 'gpt-5-mini',
      messages: [
        { 
          role: 'system', 
          content: `You are a search query parser for a job matching platform. Convert natural language queries into structured filters.

Available filters:
- search: text to search in title and description
- stage: "all", "initial" (Initial Connection), "conversation" (In Conversation), "reveal-pending" (Reveal Pending), "reveal-approved" (Reveal Approved), "meeting" (Meeting Scheduled)
- industry: "all", "Technology", "Construction", "Real Estate" - sort:"recent" (most recent first), "oldest" (oldest first), "messages" (most messages first)

Examples:
- "show me technology roles" → {search: "", stage: "all", industry: "Technology", sort: "recent"}
- "meetings scheduled this week" → {search: "", stage: "meeting", industry: "all", sort: "recent"}
- "construction projects in conversation" → {search: "", stage: "conversation", industry: "Construction", sort: "recent"}
- "senior engineering roles" → {search: "senior engineering", stage: "all", industry: "all", sort: "recent"}
- "pending reveals" → {search: "", stage: "reveal-pending", industry: "all", sort: "recent"}
- "most active conversations" → {search: "", stage: "conversation", industry: "all", sort: "messages"}

Return ONLY the JSON object, no explanation.` 
        },
        { 
          role: 'user', 
          content: query 
        },
      ],
      response_format: {
        type: 'json_schema',
        json_schema: {
          name: 'search_filters',
          schema: {
            type: 'object',
            properties: {
              search: { type: 'string', description: 'Text to search in title and description' },
              stage: { 
                type: 'string', 
                enum: ['all', 'initial', 'conversation', 'reveal-pending', 'reveal-approved', 'meeting'],
                description: 'Match stage filter'
              },
              industry: { 
                type: 'string',
                enum: ['all', 'Technology', 'Construction', 'Real Estate'],
                description: 'Industry filter'
              },
              sort: { 
                type: 'string',
                enum: ['recent', 'oldest', 'messages'],
                description: 'Sort order'
              }
            },
            required: ['search', 'stage', 'industry', 'sort'],
            additionalProperties: false,
          },
        },
      },
      reasoning_effort: 'minimal',
      verbosity: 'low',
    });

    const filters = JSON.parse(response?.choices?.[0]?.message?.content);
    return filters;
  } catch (error) {
    const errorInfo = getErrorMessage(error);
    if (errorInfo?.isInternal) {
      console.log(errorInfo?.message);
    } else {
      console.error('Error parsing natural language query:', error);
    }
    throw new Error(errorInfo.message);
  }
}

/**
 * Generates search suggestions based on partial user input.
 * @param {string} partialQuery - Partial search query.
 * @returns {Promise<string[]>} Array of suggested search queries.
 */
export async function generateSearchSuggestions(partialQuery) {
  if (!partialQuery || partialQuery?.trim()?.length < 3) {
    return [];
  }

  try {
    const response = await openai?.chat?.completions?.create({
      model: 'gpt-5-mini',
      messages: [
        { 
          role: 'system', 
          content: `You are a search suggestion generator for a job matching platform. Generate 3-5 relevant search suggestions based on partial user input.

Context: Users are searching for job matches with various stages (initial connection, in conversation, reveal pending, reveal approved, meeting scheduled) across industries (Technology, Construction, Real Estate).

Return suggestions that are:
- Natural and conversational
- Relevant to job matching and recruitment
- Diverse (mix of stages, industries, and specific roles)
- Complete sentences or phrases

Return ONLY the JSON array of suggestions, no explanation.` 
        },
        { 
          role: 'user', 
          content: `Generate search suggestions for: "${partialQuery}"` 
        },
      ],
      response_format: {
        type: 'json_schema',
        json_schema: {
          name: 'search_suggestions',
          schema: {
            type: 'object',
            properties: {
              suggestions: { 
                type: 'array',
                items: { type: 'string' },
                description: 'Array of search suggestions'
              }
            },
            required: ['suggestions'],
            additionalProperties: false,
          },
        },
      },
      reasoning_effort: 'minimal',
      verbosity: 'low',
    });

    const result = JSON.parse(response?.choices?.[0]?.message?.content);
    return result?.suggestions || [];
  } catch (error) {
    const errorInfo = getErrorMessage(error);
    if (errorInfo?.isInternal) {
      console.log(errorInfo?.message);
    } else {
      console.error('Error generating search suggestions:', error);
    }
    return [];
  }
}
